import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Camera, BookOpen, Download, Loader2, Rss, Star } from "lucide-react";
import clientApi from "@/lib/clientApi";

const CATEGORIES = [
  ["major_city", "Major cities"], ["border", "Border updates"],
  ["rest_short", "Short rest stops"], ["rest_long", "Longer rest stops"],
  ["overnight", "Overnight stops"], ["resumed", "Trip resumed"],
  ["arrival", "Arrival"], ["completion", "Trip completion"],
];

export default function ClientTripPanel({ tripId, status }) {
  const [tab, setTab] = useState("updates");
  const [prefs, setPrefs] = useState(null);
  const [feed, setFeed] = useState([]);
  const [journal, setJournal] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [access, setAccess] = useState(null);
  const [file, setFile] = useState(null);
  const [rel, setRel] = useState("pet_with_owner");
  const [consentAi, setConsentAi] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    clientApi.get(`/trips/${tripId}/my-preferences`).then((r) => setPrefs(r.data)).catch(() => {});
    clientApi.get(`/trips/${tripId}/feed`).then((r) => setFeed(r.data)).catch(() => {});
    clientApi.get(`/trips/${tripId}/journal`).then((r) => setJournal(r.data)).catch(() => setJournal([]));
    clientApi.get(`/trips/${tripId}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
    clientApi.get(`/trips/${tripId}/access`).then((r) => setAccess(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const toggleCat = (c) => setPrefs((p) => ({ ...p, categories: p.categories.includes(c) ? p.categories.filter((x) => x !== c) : [...p.categories, c] }));
  const savePrefs = async () => {
    try { await clientApi.put(`/trips/${tripId}/my-preferences`, prefs); toast.success("Your notification choices were saved"); }
    catch (e) { toast.error("Could not save"); }
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a photo");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("relationship", rel);
      fd.append("consent_private_share", true); fd.append("consent_ai_processing", consentAi);
      await clientApi.post(`/trips/${tripId}/photos`, fd);
      toast.success("Thank you! Your photo was sent to our team for review");
      setFile(null); load();
    } catch (e) { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await clientApi.get(`/trips/${tripId}/journal/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "trip-keepsake.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Keepsake not available yet"); }
  };

  if (!prefs) return null;

  const reviewTrip = async () => {
    try {
      const { data } = await clientApi.get(`/trips/${tripId}/review-link`);
      if (data.already_submitted) { toast.success("You've already reviewed this trip — thank you!"); return; }
      window.location.href = `/review/${data.token}`;
    } catch (e) { toast.error(e.response?.data?.detail || "Not available yet"); }
  };

  return (
    <div className="mt-4 border-t border-sand-200 pt-4" data-testid="client-trip-panel">
      {status === "completed" && (
        <button data-testid="review-trip-btn" onClick={reviewTrip} className="mb-3 rounded-full bg-terracotta text-white px-4 py-2 text-sm inline-flex items-center gap-1">
          <Star size={14} /> Review your trip
        </button>
      )}
      <div className="flex gap-1 mb-3 flex-wrap">
        {[["updates", "Updates", Rss], ["journal", "Journal", BookOpen], ["photos", "Add photos", Camera], ["prefs", "Notifications", Bell]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`client-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-xs rounded-full flex items-center gap-1 ${tab === k ? "bg-terracotta text-white" : "bg-sand-100 text-charcoal-600"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "updates" && (
        <div className="space-y-2">
          {feed.map((e) => (
            <div key={e.id} className="text-sm bg-sand-50 rounded-lg p-3" data-testid={`client-event-${e.id}`}>
              <div className="font-medium text-charcoal-800 flex items-center gap-2">{e.title}
                {e.delayed && <span className="text-[10px] px-2 rounded-full bg-amber-100 text-amber-700">delayed</span>}</div>
              <div className="text-charcoal-600 text-xs whitespace-pre-line">{e.body}</div>
            </div>
          ))}
          {feed.length === 0 && <p className="text-sm text-charcoal-500">No updates yet — we'll post milestones here as they're detected.</p>}
        </div>
      )}

      {tab === "journal" && (
        <div className="space-y-3">
          {access && (
            <div className="flex items-center justify-between text-xs text-charcoal-500 bg-sand-50 rounded-lg p-2">
              <span>{access.journal_expires_at ? `Access until ${new Date(access.journal_expires_at).toLocaleDateString()}` : "Available throughout the trip"}</span>
              <button data-testid="client-keepsake-btn" onClick={downloadPdf} className="text-terracotta flex items-center gap-1"><Download size={13} /> Keepsake PDF</button>
            </div>
          )}
          {journal.map((e) => (
            <div key={e.id} className="text-sm bg-white border border-sand-200 rounded-lg p-3" data-testid={`client-journal-${e.id}`}>
              <div className="font-medium text-charcoal-800">{e.title}</div>
              <div className="text-charcoal-600 text-xs">{e.body}</div>
              {e.fact_source && <a href={e.fact_source} target="_blank" rel="noreferrer" className="text-[11px] text-terracotta">Source</a>}
            </div>
          ))}
          {journal.length === 0 && <p className="text-sm text-charcoal-500">No journal entries published yet.</p>}
        </div>
      )}

      {tab === "photos" && (
        <div className="space-y-3">
          <p className="text-xs text-charcoal-500">Share a photo of your pet. Our team reviews every photo before it appears. Your original is kept and location data is removed from shared copies.</p>
          <input data-testid="client-photo-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          <div className="flex flex-wrap gap-3 text-xs items-center">
            <select data-testid="client-photo-rel" className="border rounded-lg px-2 py-1" value={rel} onChange={(e) => setRel(e.target.value)}>
              <option value="pet_with_owner">Pet with owner</option>
              <option value="pet_with_team">Pet with our team</option>
            </select>
            <label className="flex items-center gap-1"><input type="checkbox" checked={consentAi} onChange={(e) => setConsentAi(e.target.checked)} /> Allow AI souvenir use</label>
          </div>
          <button data-testid="client-photo-upload" onClick={upload} disabled={busy} className="bg-terracotta text-white rounded-full px-4 py-1.5 text-sm flex items-center gap-1 disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />} Send photo
          </button>
          <div className="grid grid-cols-3 gap-2 pt-2">
            {photos.map((p) => (
              <div key={p.id} className="rounded-lg overflow-hidden border border-sand-200 bg-sand-50 h-24 flex items-center justify-center text-[10px] text-charcoal-400" data-testid={`client-photo-${p.id}`}>
                {p.caption || "Photo"}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "prefs" && (
        <div className="space-y-3">
          <p className="text-xs text-charcoal-500">Choose which updates you'd like and how to receive them. You can change these anytime.</p>
          <div className="flex flex-wrap gap-2 text-xs">
            {CATEGORIES.map(([c, label]) => (
              <label key={c} className="flex items-center gap-1"><input data-testid={`client-cat-${c}`} type="checkbox" checked={prefs.categories.includes(c)} onChange={() => toggleCat(c)} /> {label}</label>
            ))}
          </div>
          <div className="flex gap-4 text-xs items-center">
            <span className="text-charcoal-400">Receive by:</span>
            {["portal", "email"].map((ch) => (
              <label key={ch} className="flex items-center gap-1"><input type="checkbox" checked={(prefs.channel || []).includes(ch)} onChange={() => setPrefs((p) => ({ ...p, channel: p.channel.includes(ch) ? p.channel.filter((x) => x !== ch) : [...p.channel, ch] }))} /> {ch}</label>
            ))}
            <select className="border rounded-lg px-2 py-1" value={prefs.language} onChange={(e) => setPrefs({ ...prefs, language: e.target.value })}>
              <option value="en">English</option><option value="es">Español</option>
            </select>
          </div>
          <button data-testid="client-prefs-save" onClick={savePrefs} className="bg-terracotta text-white rounded-full px-4 py-1.5 text-sm">Save my choices</button>
        </div>
      )}
    </div>
  );
}
